/**
 * Profile / Settings screen.
 *
 * Lets the signed-in user edit their display name (PATCH /auth/me via
 * api.updateMe), shows their email read-only, the language switcher, and a sign
 * out button. On a successful save it reloads `me` (FamilyContext) so the new
 * name appears everywhere it's read from `me.user` (top bar, user menu, …).
 *
 * No business logic lives here beyond client-side validation that mirrors the
 * shared `updateProfileInputSchema` (trim, 1..120 chars); the server is the
 * source of truth and re-validates.
 */
import { useState, type FormEvent } from 'react';
import { ApiClientError } from '@solandia/core';
import { useI18n } from '../i18n';
import { useApi } from '../api/ApiProvider';
import { useFamily } from '../family/FamilyContext';
import { useTour } from '../tour/TourContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { SectionHeader } from '../components/ui';
import { signOut } from '../auth/authClient';
import { FamilyModeCard } from './FamilyModeCard';

const MAX_NAME = 120;

export function SettingsScreen() {
  const { t } = useI18n();
  const api = useApi();
  const { me, reloadMe } = useFamily();
  const { startTour } = useTour();

  const [name, setName] = useState(me.user.displayName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const trimmed = name.trim();
  const invalid = trimmed.length < 1 || trimmed.length > MAX_NAME;
  const dirty = trimmed !== me.user.displayName.trim();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (invalid || saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.updateMe({ displayName: trimmed });
      // Refetch `me` so the resolved name updates across the app (top bar, menu).
      reloadMe();
      setSaved(true);
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === 'unprocessable_entity'
          ? t.settings.nameInvalid
          : t.errors.generic,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="settings stack">
      <SectionHeader title={t.settings.title} subtitle={t.settings.lead} />

      {/* Profile card — editable display name + read-only email. */}
      <section className="card stack" aria-labelledby="settings-profile-heading">
        <h2 id="settings-profile-heading" className="section-title">
          {t.settings.profile}
        </h2>

        <form className="stack" onSubmit={onSubmit} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="settings-name">
              {t.settings.displayName}
            </label>
            <input
              id="settings-name"
              className="input"
              type="text"
              value={name}
              maxLength={MAX_NAME}
              autoComplete="name"
              aria-invalid={invalid && dirty}
              aria-describedby="settings-name-hint"
              onChange={(e) => {
                setName(e.target.value);
                setSaved(false);
                setError(null);
              }}
            />
            <span id="settings-name-hint" className="field-hint">
              {t.settings.displayNameHint}
            </span>
            {invalid && dirty ? (
              <span className="field-error" role="alert">
                {t.settings.nameInvalid}
              </span>
            ) : null}
          </div>

          <div className="field">
            <label className="field-label" htmlFor="settings-email">
              {t.settings.email}
            </label>
            <input
              id="settings-email"
              className="input"
              type="email"
              value={me.user.email}
              readOnly
              aria-describedby="settings-email-hint"
            />
            <span id="settings-email-hint" className="field-hint">
              {t.settings.emailHint}
            </span>
          </div>

          {error ? (
            <div className="banner banner-danger" role="alert">
              <span aria-hidden="true">⚠️</span>
              <div className="grow">{error}</div>
            </div>
          ) : null}

          <div className="row" style={{ justifyContent: 'flex-start' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || invalid || !dirty}
            >
              {saving ? t.common.saving : t.common.save}
            </button>
            {/* Polite live region: announces the save without stealing focus. */}
            <span role="status" aria-live="polite" className="text-sm muted">
              {saved && !dirty ? t.settings.saved : ''}
            </span>
          </div>
        </form>
      </section>

      {/* Preferences card — language. */}
      <section className="card stack" aria-labelledby="settings-prefs-heading">
        <h2 id="settings-prefs-heading" className="section-title">
          {t.settings.preferences}
        </h2>
        <div className="field">
          <span className="field-label" id="settings-language-label">
            {t.language.label}
          </span>
          <LanguageSwitcher />
        </div>
      </section>

      {/* Help card — replay the onboarding tour at will. */}
      <section className="card stack" aria-labelledby="settings-tour-heading">
        <h2 id="settings-tour-heading" className="section-title">
          {t.tour.help}
        </h2>
        <p className="lead">{t.tour.helpHint}</p>
        <div className="row" style={{ justifyContent: 'flex-start' }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => startTour()}
          >
            <span aria-hidden="true">🧭</span> {t.tour.start}
          </button>
        </div>
      </section>

      {/* Account card — sign out. */}
      <section className="card stack" aria-labelledby="settings-account-heading">
        <h2 id="settings-account-heading" className="section-title">
          {t.settings.account}
        </h2>
        <p className="lead">{t.settings.signOutHint}</p>
        <div className="row" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn btn-danger" onClick={signOut}>
            {t.auth.signOut}
          </button>
        </div>
      </section>

      {/* Režim rodiny — quiet, parent-only, lives at the very bottom (Phase 3). */}
      <FamilyModeCard />
    </div>
  );
}
